import React, { useState, useEffect } from 'react';
import './SurveyModule.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function SurveyModule() {
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    minRating: '',
    maxRating: ''
  });
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.minRating) params.append('minRating', filters.minRating);
      if (filters.maxRating) params.append('maxRating', filters.maxRating);

      const response = await fetch(`/api/survey/list?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch surveys');
      }

      setSurveys(data.surveys);
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchSurveys();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      minRating: '',
      maxRating: ''
    });
    setTimeout(() => fetchSurveys(), 100);
  };

  const handleDeleteSurvey = async (id) => {
    if (!window.confirm('Are you sure you want to delete this survey response?')) return;

    try {
      const response = await fetch(`/api/survey/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete survey');
      }

      setSurveys(prev => prev.filter(s => s.id !== id));
      setSelectedSurvey(null);
      fetchSurveys(); // Refresh stats
    } catch (err) {
      alert('Error deleting survey: ' + err.message);
    }
  };

  const renderStars = (rating) => {
    if (!rating) return '-';
    return '★'.repeat(rating) + '☆'.repeat(6 - rating);
  };

  const getNPSCategory = (score) => {
    if (score === null || score === undefined) return '-';
    if (score >= 9) return { label: 'Promoter', color: '#4caf50' };
    if (score >= 7) return { label: 'Passive', color: '#ff9800' };
    return { label: 'Detractor', color: '#f44336' };
  };

  const getQualityColor = (quality) => {
    const colors = {
      'Very Satisfied': '#4caf50',
      'Satisfied': '#8bc34a',
      'Neutral': '#ff9800',
      'Dissatisfied': '#ff5722',
      'Very Dissatisfied': '#f44336'
    };
    return colors[quality] || '#666';
  };

  return (
    <div className="survey-module">
      <div className="survey-header">
        <h1>📊 Customer Satisfaction Surveys</h1>
        <p>View and analyze customer feedback submissions</p>
      </div>

      {/* Statistics Dashboard */}
      {stats && (
        <div className="stats-dashboard">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Responses</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.averageOverallRating || '-'}</div>
            <div className="stat-label">Avg Rating (out of 5)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.averageNPS || '-'}</div>
            <div className="stat-label">Avg NPS Score (out of 10)</div>
          </div>
        </div>
      )}

      {/* Label Quality Breakdown */}
      {stats && stats.labelQualityBreakdown && (
        <div className="quality-breakdown">
          <h3>Label Quality Breakdown</h3>
          <div className="quality-bars">
            {Object.entries(stats.labelQualityBreakdown).map(([quality, count]) => (
              <div key={quality} className="quality-bar-item">
                <div className="quality-label">{quality}</div>
                <div className="quality-bar-container">
                  <div 
                    className="quality-bar-fill" 
                    style={{ 
                      width: `${stats.total > 0 ? (count / stats.total) * 100 : 0}%`,
                      background: getQualityColor(quality)
                    }}
                  />
                </div>
                <div className="quality-count">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filter Surveys</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label>Min Rating</label>
            <select name="minRating" value={filters.minRating} onChange={handleFilterChange}>
              <option value="">Any</option>
              <option value="1">1 Star</option>
              <option value="2">2 Stars</option>
              <option value="3">3 Stars</option>
              <option value="4">4 Stars</option>
              <option value="5">5 Stars</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Max Rating</label>
            <select name="maxRating" value={filters.maxRating} onChange={handleFilterChange}>
              <option value="">Any</option>
              <option value="1">1 Star</option>
              <option value="2">2 Stars</option>
              <option value="3">3 Stars</option>
              <option value="4">4 Stars</option>
              <option value="5">5 Stars</option>
            </select>
          </div>
        </div>
        <div className="filter-buttons">
          <button onClick={handleApplyFilters} className="btn-apply">Apply Filters</button>
          <button onClick={handleResetFilters} className="btn-reset">Reset</button>
        </div>
      </div>

      {/* Survey List */}
      <div className="surveys-section">
        <h3>Survey Responses ({surveys.length})</h3>
        
        {loading && <div className="loading">Loading surveys...</div>}
        {error && <div className="error-message">{error}</div>}
        
        {!loading && !error && surveys.length === 0 && (
          <div className="no-surveys">No survey responses found</div>
        )}

        {!loading && !error && surveys.length > 0 && (
          <div className="surveys-table-container">
            <table className="surveys-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Company</th>
                  <th>Label Quality</th>
                  <th>Turnaround</th>
                  <th>Team</th>
                  <th>On Time</th>
                  <th>Rating</th>
                  <th>NPS</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map(survey => (
                  <tr key={survey.id} onClick={() => setSelectedSurvey(survey)} className="survey-row">
                    <td>{new Date(survey.submittedAt).toLocaleDateString()}</td>
                    <td>{survey.companyName || 'Anonymous'}</td>
                    <td>
                      <span className="quality-badge" style={{ background: getQualityColor(survey.labelQuality) }}>
                        {survey.labelQuality}
                      </span>
                    </td>
                    <td>{survey.turnaroundTime}</td>
                    <td>{survey.teamResponsiveness}</td>
                    <td>{survey.deliveryOnTime ? '✓ Yes' : '✗ No'}</td>
                    <td>{renderStars(survey.overallRating)}</td>
                    <td>
                      {survey.recommendationScore !== null && survey.recommendationScore !== undefined ? (
                        <span 
                          className="nps-badge" 
                          style={{ background: getNPSCategory(survey.recommendationScore).color }}
                        >
                          {survey.recommendationScore}/10
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteSurvey(survey.id); }}
                        className="btn-delete-small"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Survey Detail Modal */}
      {selectedSurvey && (
        <div className="survey-modal-overlay" onClick={() => setSelectedSurvey(null)}>
          <div className="survey-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Survey Details</h2>
              <button onClick={() => setSelectedSurvey(null)} className="close-btn">✕</button>
            </div>
            <div className="modal-content">
              <div className="detail-row">
                <strong>Submitted:</strong>
                <span>{new Date(selectedSurvey.submittedAt).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <strong>Company:</strong>
                <span>{selectedSurvey.companyName || 'Anonymous'}</span>
              </div>
              {selectedSurvey.contactPerson && (
                <div className="detail-row">
                  <strong>Contact Person:</strong>
                  <span>{selectedSurvey.contactPerson}</span>
                </div>
              )}
              <div className="detail-row">
                <strong>Label Quality:</strong>
                <span className="quality-badge" style={{ background: getQualityColor(selectedSurvey.labelQuality) }}>
                  {selectedSurvey.labelQuality}
                </span>
              </div>
              <div className="detail-row">
                <strong>Turnaround Time:</strong>
                <span>{selectedSurvey.turnaroundTime}</span>
              </div>
              <div className="detail-row">
                <strong>Team Responsiveness:</strong>
                <span>{selectedSurvey.teamResponsiveness}</span>
              </div>
              <div className="detail-row">
                <strong>Delivery On Time:</strong>
                <span>{selectedSurvey.deliveryOnTime ? '✓ Yes' : '✗ No'}</span>
              </div>
              {selectedSurvey.deliveryExplanation && (
                <div className="detail-row">
                  <strong>Delivery Explanation:</strong>
                  <span>{selectedSurvey.deliveryExplanation}</span>
                </div>
              )}
              {selectedSurvey.overallRating && (
                <div className="detail-row">
                  <strong>Overall Rating:</strong>
                  <span>{renderStars(selectedSurvey.overallRating)} ({selectedSurvey.overallRating}/6)</span>
                </div>
              )}
              {selectedSurvey.recommendationScore !== null && selectedSurvey.recommendationScore !== undefined && (
                <div className="detail-row">
                  <strong>Recommendation Score:</strong>
                  <span>
                    {selectedSurvey.recommendationScore}/10 
                    <span 
                      className="nps-category" 
                      style={{ color: getNPSCategory(selectedSurvey.recommendationScore).color }}
                    >
                      ({getNPSCategory(selectedSurvey.recommendationScore).label})
                    </span>
                  </span>
                </div>
              )}
              {selectedSurvey.suggestions && (
                <div className="detail-row suggestions">
                  <strong>Suggestions:</strong>
                  <p>{selectedSurvey.suggestions}</p>
                </div>
              )}
              {selectedSurvey.mayContact && (
                <div className="contact-info">
                  <strong>Contact Information:</strong>
                  {selectedSurvey.contactEmail && <div>Email: {selectedSurvey.contactEmail}</div>}
                  {selectedSurvey.contactPhone && <div>Phone: {selectedSurvey.contactPhone}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SurveyModule;
